import {
    buildGetWidgetsList,
    buildGetWidgetType,
    buildGetWidgetStatus,
    buildGetWidgetFunction,
    buildGetWidgetSubIdList,
    buildButtonCommand,
    buildSliderCommand,
    buildMatrixColorCommand,
    buildMatrixColorChangeCommand,
    buildMatrixSliderCommand,
    buildMatrixSliderChangeCommand,
    buildMatrixComboCommand,
    buildMatrixPushButtonCommand,
    buildSpeedDialFactorCommand,
    buildSpeedDialTimeCommand,
    buildSpeedDialStepCommand,
    buildAnimationPresetCommand,
    buildAnimationSubControlCommand,
    buildAnimationControlApiCommand,
    parseWidgetsList,
    parseWidgetTypeResponse,
    parseWidgetStatusResponse,
    parseWidgetFunctionResponse,
    parseWidgetSubIdListResponse,
    parseFunctionStatusUpdate,
    parseButtonStatusUpdate,
    parseMatrixLiveUpdate,
    parseSpeedDialLiveUpdate,
    parseLiveWidgetUpdate,
} from '../utils/qlcProtocol';

interface ThrottleEntry {
    lastSentAt: number;
    timer: ReturnType<typeof setTimeout> | null;
    pendingValue: number | null;
}

const sliderThrottleMap = new Map<string, ThrottleEntry>();

// Shared singleton WebSocket state across all components
const socket = ref<WebSocket | null>(null);
const reconnectTimer = ref<ReturnType<typeof setTimeout> | undefined>(undefined);
const isConnecting = ref(false);

export function useQlcPlus() {
    const qlcStore = useQlcStore();
    const { notify } = useNotification();

    // Haptic feedback helper for mobile dancefloor control
    function triggerHaptic(pattern: number | number[] = 25): void {
        if (typeof window !== 'undefined' && 'navigator' in window && 'vibrate' in navigator) {
            try {
                navigator.vibrate(pattern);
            }
            catch {
                // Ignore vibration errors if blocked by browser policy
            }
        }
    }

    // Direct WebSocket send
    function sendRaw(message: string): boolean {
        if (qlcStore.isMockMode) {
            return true;
        }

        if (socket.value && socket.value.readyState === WebSocket.OPEN) {
            socket.value.send(message);
            return true;
        }
        return false;
    }

    // Direct slider message dispatch
    function sendDirectSlider(widgetId: string, value: number): void {
        qlcStore.setWidgetValue(widgetId, value);
        const cmd = buildSliderCommand(widgetId, value);
        sendRaw(cmd);
    }

    // Throttled slider dispatch (maximum 1 message every 25ms / 40Hz)
    function setFaderValue(widgetId: string, value: number): void {
        // Immediate local store update for snappy 60fps UI feedback
        qlcStore.setWidgetValue(widgetId, value);

        if (qlcStore.isMockMode) {
            return;
        }

        const now = Date.now();
        let entry = sliderThrottleMap.get(widgetId);
        if (!entry) {
            entry = {
                lastSentAt: 0,
                timer: null,
                pendingValue: null,
            };
            sliderThrottleMap.set(widgetId, entry);
        }

        entry.pendingValue = value;
        const elapsed = now - entry.lastSentAt;

        if (elapsed >= 25) {
            if (entry.timer) {
                clearTimeout(entry.timer);
                entry.timer = null;
            }
            sendDirectSlider(widgetId, value);
            entry.lastSentAt = now;
            entry.pendingValue = null;
        }
        else if (!entry.timer) {
            const remaining = 25 - elapsed;
            entry.timer = setTimeout(() => {
                if (entry && entry.pendingValue !== null) {
                    sendDirectSlider(widgetId, entry.pendingValue);
                    entry.lastSentAt = Date.now();
                    entry.pendingValue = null;
                    entry.timer = null;
                }
            }, remaining);
        }
    }

    // Press button (momentary or start of press)
    function pressButton(widgetId: string): void {
        triggerHaptic(30);

        qlcStore.setWidgetValue(widgetId, 255);

        if (!qlcStore.isMockMode) {
            sendRaw(buildButtonCommand(widgetId, true));
        }
    }

    // Release button (momentary flash release)
    function releaseButton(widgetId: string): void {
        qlcStore.setWidgetValue(widgetId, 0);

        if (!qlcStore.isMockMode) {
            sendRaw(buildButtonCommand(widgetId, false));
        }
    }

    // Toggle button on/off
    function toggleButton(widgetId: string): void {
        const currentVal = qlcStore.widgets[widgetId]?.value ?? 0;
        const willTurnOn = currentVal === 0;

        triggerHaptic(willTurnOn ? [35, 20, 25] : 20);

        if (willTurnOn) {
            qlcStore.setWidgetValue(widgetId, 255);
            if (!qlcStore.isMockMode) {
                sendRaw(buildButtonCommand(widgetId, true));
            }
        }
        else {
            qlcStore.setWidgetValue(widgetId, 0);
            if (!qlcStore.isMockMode) {
                sendRaw(buildButtonCommand(widgetId, false));
            }
        }
    }

    // Select RGB Matrix animation preset / sub-control
    function selectAnimationPreset(widgetId: string, presetId: string): void {
        triggerHaptic(25);
        qlcStore.setAnimationState(widgetId, { selectedPresetId: presetId });

        if (!qlcStore.isMockMode) {
            sendRaw(buildAnimationPresetCommand(widgetId, presetId));
            sendRaw(buildAnimationControlApiCommand(widgetId, presetId));
        }
    }

    // Trigger animation sub-control directly (pushbutton on Matrix)
    function triggerAnimationControl(widgetId: string, controlId: string): void {
        triggerHaptic(20);
        if (!qlcStore.isMockMode) {
            sendRaw(buildAnimationSubControlCommand(widgetId, controlId));
            sendRaw(buildAnimationControlApiCommand(widgetId, controlId));
        }
    }

    // Set RGB Matrix color for a specific slot (1 to 5)
    // Command format: <WidgetID>|MATRIX_COLOR_<1-5>|<HEX_CODE>
    function setMatrixColor(
        widgetId: string,
        slot: 1 | 2 | 3 | 4 | 5,
        hex: string,
    ): void {
        triggerHaptic(20);
        const formattedHex = hex.startsWith('#') ? hex : `#${hex}`;

        const currentColors = qlcStore.animationStates[widgetId]?.colors || [
            '#00f0ff',
            '#ff0077',
            '#ffff00',
            '#00ff00',
            '#9900ff',
        ];
        const newColors = [...currentColors] as [string, string, string, string, string];
        newColors[slot - 1] = formattedHex;

        qlcStore.setAnimationState(widgetId, {
            colors: newColors,
            primaryColor: newColors[0],
            secondaryColor: newColors[1],
        });

        if (qlcStore.isMockMode) {
            return;
        }

        // 1. Direct command requested: {widget_id}|MATRIX_COLOR_{1-5}|{HEX_CODE}
        sendRaw(buildMatrixColorCommand(widgetId, slot, formattedHex));

        // 2. Native QLC+ C++ WebAccess command: {widget_id}|MATRIX_COLOR_CHANGE|COLOR_{1-5}|{INT_VALUE}
        sendRaw(buildMatrixColorChangeCommand(widgetId, slot, formattedHex));
    }

    // Set RGB Matrix colors (backward compatibility helper for primary / secondary)
    function setAnimationColors(
        widgetId: string,
        primaryHex: string,
        secondaryHex: string,
        targetSlot?: 'primary' | 'secondary',
        controlId?: string,
    ): void {
        if (targetSlot === 'primary' || !targetSlot) {
            setMatrixColor(widgetId, 1, primaryHex);
        }
        if (targetSlot === 'secondary' || !targetSlot) {
            setMatrixColor(widgetId, 2, secondaryHex);
        }

        if (controlId && !qlcStore.isMockMode) {
            sendRaw(buildMatrixPushButtonCommand(widgetId, controlId));
        }
    }

    // Set RGB Matrix speed (0 to 255)
    function setAnimationSpeed(widgetId: string, speed: number): void {
        qlcStore.setAnimationState(widgetId, { speed });
        qlcStore.setWidgetValue(widgetId, speed);

        if (!qlcStore.isMockMode) {
            sendRaw(buildMatrixSliderCommand(widgetId, speed));
            sendRaw(buildMatrixSliderChangeCommand(widgetId, speed));
        }
    }

    // Set RGB Matrix algorithm pattern by ID (e.g. 34 for "One By One", 13 for "Even/Odd", etc.)
    function setMatrixAlgorithm(widgetId: string, algorithmId: number): void {
        triggerHaptic(20);
        qlcStore.setAnimationState(widgetId, { algorithmId });
        if (!qlcStore.isMockMode) {
            sendRaw(buildMatrixComboCommand(widgetId, algorithmId));
        }
    }

    // Broadcast global colors to all or specified matrix widgets simultaneously
    function broadcastGlobalColors(
        color1: string,
        color2: string,
        targetWidgetIds?: string[],
    ): void {
        triggerHaptic([25, 20]);
        const targets = targetWidgetIds && targetWidgetIds.length > 0
            ? targetWidgetIds
            : Object.values(qlcStore.widgets)
                .filter(w => w.type === 'Animation')
                .map(w => w.id);

        for (const id of targets) {
            setMatrixColor(id, 1, color1);
            setMatrixColor(id, 2, color2);
        }
    }

    // Turn matrix widget on/off (supports button trigger, matrix slider, and function status)
    function setMatrixPower(widgetId: string, turnOn: boolean): void {
        triggerHaptic(turnOn ? [35, 20, 25] : 20);
        const value = turnOn ? 255 : 0;
        qlcStore.setWidgetValue(widgetId, value);

        if (qlcStore.isMockMode) {
            return;
        }

        // 1. Send the exact command used by QLC+ web virtual console slider: {id}|MATRIX_SLIDER|{value}
        sendRaw(buildMatrixSliderCommand(widgetId, value));

        // 2. Also send MATRIX_SLIDER_CHANGE and direct button value for complete compatibility
        sendRaw(buildMatrixSliderChangeCommand(widgetId, value));
        sendRaw(buildButtonCommand(widgetId, turnOn));

        // 3. If mapped to a QLC+ function, update function status directly
        const funcId = qlcStore.widgets[widgetId]?.functionId;
        if (funcId) {
            sendRaw(`QLC+API|setFunctionStatus|${funcId}|${turnOn ? 1 : 0}`);
        }
    }

    // Toggle matrix widget power state
    function toggleMatrix(widgetId: string): void {
        const currentVal = qlcStore.widgets[widgetId]?.value ?? 0;
        const willTurnOn = currentVal === 0;
        setMatrixPower(widgetId, willTurnOn);
    }

    // Send SpeedDial multiplier / factor index (2: 1/16 .. 6: 1 .. 10: 16)
    function sendSpeedDialFactor(widgetId: string, factorIndex: number): void {
        triggerHaptic(15);
        qlcStore.setSpeedDialState(widgetId, { factorIndex });

        if (!qlcStore.isMockMode) {
            sendRaw(buildSpeedDialFactorCommand(widgetId, factorIndex));
        }
    }

    // Send SpeedDial duration in milliseconds (e.g. from Tap tempo or BPM adjustment)
    function sendSpeedDialTime(widgetId: string, timeMs: number): void {
        const clamped = Math.max(0, Math.round(timeMs));
        qlcStore.setSpeedDialState(widgetId, { timeMs: clamped });

        if (!qlcStore.isMockMode) {
            sendRaw(buildSpeedDialTimeCommand(widgetId, clamped));
        }
    }

    // Send SpeedDial step command: SPEED_UP or SPEED_DOWN
    function sendSpeedDialStep(widgetId: string, direction: 'up' | 'down'): void {
        triggerHaptic(20);

        if (!qlcStore.isMockMode) {
            sendRaw(buildSpeedDialStepCommand(widgetId, direction));
        }
    }

    // Global Panic / Blackout
    function triggerBlackout(): void {
        triggerHaptic([80, 50, 100]);
        // Set all buttons and sliders to 0 in store
        for (const id of Object.keys(qlcStore.widgets)) {
            qlcStore.setWidgetValue(id, 0);
            if (!qlcStore.isMockMode) {
                sendRaw(buildButtonCommand(id, false));
                sendRaw(buildSliderCommand(id, 0));
            }
        }
    }

    // Request full discovery of Virtual Console widgets
    function requestWidgetsDiscovery(): void {
        if (qlcStore.isMockMode) {
            return;
        }
        sendRaw(buildGetWidgetsList());
    }

    // Handle incoming WebSocket messages from QLC+
    function handleIncomingMessage(rawMessage: string): void {
        qlcStore.connection.lastMessageAt = Date.now();

        // Check if message is a JSON envelope from server
        if (rawMessage.startsWith('{') && rawMessage.endsWith('}')) {
            try {
                const parsed = JSON.parse(rawMessage);
                if (parsed.type === 'connected') {
                    qlcStore.setConnectionStatus({
                        connected: true,
                        mode: parsed.mode || 'pi',
                        pairingCode: parsed.pairingCode || qlcStore.connection.pairingCode || null,
                        statusText: parsed.mode === 'pi'
                            ? 'Verbonden met Raspberry Pi (Lokaal)'
                            : (parsed.needsPairing
                                ? 'Verbonden met Cloud Server (Koppelcode vereist)'
                                : 'Verbonden met Cloud Server'),
                    });
                    if (parsed.mode === 'pi') {
                        requestWidgetsDiscovery();
                    }
                    return;
                }
                if (parsed.type === 'cloud_pairing_code') {
                    qlcStore.setConnectionStatus({
                        pairingCode: parsed.pairingCode || null,
                    });
                    return;
                }
                if (parsed.type === 'paired') {
                    qlcStore.setConnectionStatus({
                        connected: true,
                        mode: 'cloud',
                        pairingCode: parsed.pairingCode,
                        statusText: `Gekoppeld via Cloud Relay (${parsed.pairingCode})`,
                    });
                    if (typeof window !== 'undefined') {
                        try {
                            localStorage.setItem('qlc_cloud_pairing_code', parsed.pairingCode);
                        }
                        catch {
                            // Ignore localStorage errors
                        }
                    }
                    notify({
                        title: 'Koppelen gelukt!',
                        text: `Succesvol verbonden met Raspberry Pi (code: ${parsed.pairingCode})`,
                        type: 'success',
                    });
                    requestWidgetsDiscovery();
                    return;
                }
                if (parsed.type === 'pi_disconnected') {
                    qlcStore.setConnectionStatus({
                        connected: false,
                        pairingCode: null,
                        statusText: 'Raspberry Pi tunnel is verbroken. Opnieuw koppelen...',
                    });
                    notify({
                        title: 'Verbinding verbroken',
                        text: 'De verbinding met de Raspberry Pi tunnel is verbroken.',
                        type: 'warn',
                    });
                    return;
                }
                if (parsed.type === 'error') {
                    qlcStore.setConnectionStatus({
                        pairingCode: (parsed.code === 'PAIRING_FAILED' || parsed.code === 'NOT_PAIRED') ? null : qlcStore.connection.pairingCode,
                        statusText: parsed.message || 'Fout opgetreden',
                    });
                    notify({
                        title: 'Koppelen mislukt',
                        text: parsed.message || 'Kon niet koppelen met de Raspberry Pi.',
                        type: 'error',
                    });
                    return;
                }
            }
            catch {
                // Not JSON, continue with QLC+ protocol parsing
            }
        }

        // 1. Check for animation matrix updates: "<WidgetID>|MATRIX_COLOR_<1-5>|<#hex>" or slider/combo
        const matrixUpdate = parseMatrixLiveUpdate(rawMessage);
        if (matrixUpdate) {
            if (matrixUpdate.type === 'color' && matrixUpdate.slot && matrixUpdate.colorHex) {
                const currentColors = qlcStore.animationStates[matrixUpdate.widgetId]?.colors || [
                    '#00f0ff',
                    '#ff0077',
                    '#ffff00',
                    '#00ff00',
                    '#9900ff',
                ];
                const newColors = [...currentColors] as [string, string, string, string, string];
                newColors[matrixUpdate.slot - 1] = matrixUpdate.colorHex;
                qlcStore.setAnimationState(matrixUpdate.widgetId, {
                    colors: newColors,
                    primaryColor: newColors[0],
                    secondaryColor: newColors[1],
                });
            }
            else if (matrixUpdate.type === 'slider' && matrixUpdate.sliderValue !== undefined) {
                qlcStore.setAnimationState(matrixUpdate.widgetId, {
                    speed: matrixUpdate.sliderValue,
                });
                qlcStore.setWidgetValue(matrixUpdate.widgetId, matrixUpdate.sliderValue);
            }
            else if (matrixUpdate.type === 'combo' && matrixUpdate.comboValue !== undefined) {
                const parsedId = Number.parseInt(matrixUpdate.comboValue, 10);
                if (!Number.isNaN(parsedId)) {
                    qlcStore.setAnimationState(matrixUpdate.widgetId, {
                        algorithmId: parsedId,
                    });
                }
            }
            return;
        }

        // 2. Check for SpeedDial updates: "<WidgetID>|SPEED_STATE|<bpm>|<factor>", "<WidgetID>|SPEED_FACTOR|<idx>" or "<WidgetID>|SPEED_TIME|<ms>"
        const speedDialUpdate = parseSpeedDialLiveUpdate(rawMessage);
        if (speedDialUpdate) {
            if (speedDialUpdate.type === 'state') {
                qlcStore.setSpeedDialState(speedDialUpdate.widgetId, {
                    factorIndex: speedDialUpdate.factorIndex,
                    bpm: speedDialUpdate.bpm,
                    timeMs: speedDialUpdate.timeMs,
                });
            }
            else if (speedDialUpdate.type === 'factor' && speedDialUpdate.factorIndex !== undefined) {
                qlcStore.setSpeedDialState(speedDialUpdate.widgetId, {
                    factorIndex: speedDialUpdate.factorIndex,
                });
            }
            else if (speedDialUpdate.type === 'time' && speedDialUpdate.timeMs !== undefined) {
                qlcStore.setSpeedDialState(speedDialUpdate.widgetId, {
                    timeMs: speedDialUpdate.timeMs,
                    bpm: speedDialUpdate.bpm,
                });
            }
            return;
        }

        // 3. Check for real-time FUNCTION status updates: "FUNCTION|<id>|Running" or "FUNCTION|<id>|Stopped"
        const funcUpdate = parseFunctionStatusUpdate(rawMessage);
        if (funcUpdate) {
            qlcStore.handleFunctionState(funcUpdate.functionId, funcUpdate.status === 'Running');
            return;
        }

        // 4. Check for BUTTON updates: "<WidgetID>|BUTTON|<0/255>"
        const btnUpdate = parseButtonStatusUpdate(rawMessage);
        if (btnUpdate) {
            qlcStore.setWidgetValue(btnUpdate.widgetId, btnUpdate.value);
            return;
        }

        // 5. Check for live value updates: "<WidgetID>|<Value>"
        const liveUpdate = parseLiveWidgetUpdate(rawMessage);
        if (liveUpdate) {
            qlcStore.setWidgetValue(liveUpdate.widgetId, liveUpdate.value);
            return;
        }

        // 6. Check for getWidgetsList response
        if (rawMessage.includes('getWidgetsList')) {
            console.log('[useQlcPlus] Received getWidgetsList payload:', rawMessage);
            const list = parseWidgetsList(rawMessage);
            console.log(`[useQlcPlus] Parsed ${list.length} widgets from QLC+:`, list);
            for (const item of list) {
                qlcStore.setWidget({
                    id: item.id,
                    name: item.name,
                    type: 'Unknown',
                    value: 0,
                });
                // Fetch widget type, current status, and associated function
                sendRaw(buildGetWidgetType(item.id));
                sendRaw(buildGetWidgetStatus(item.id));
                sendRaw(buildGetWidgetFunction(item.id));
            }
            return;
        }

        // 7. Check for getWidgetType response
        if (rawMessage.includes('getWidgetType')) {
            const typeResult = parseWidgetTypeResponse(rawMessage);
            if (typeResult) {
                const existing = qlcStore.widgets[typeResult.widgetId];
                if (existing) {
                    existing.type = typeResult.type;
                }
                // If Animation, request sub-ids
                if (typeResult.type === 'Animation') {
                    sendRaw(buildGetWidgetSubIdList(typeResult.widgetId));
                }
            }
            return;
        }

        // 8. Check for getWidgetFunction response
        if (rawMessage.includes('getWidgetFunction')) {
            const funcResult = parseWidgetFunctionResponse(rawMessage);
            if (funcResult) {
                qlcStore.setWidgetFunction(funcResult.widgetId, funcResult.functionId);
            }
            return;
        }

        // 9. Check for getWidgetStatus response
        if (rawMessage.includes('getWidgetStatus')) {
            const statusResult = parseWidgetStatusResponse(rawMessage);
            if (statusResult) {
                qlcStore.setWidgetValue(statusResult.widgetId, statusResult.value);
            }
            return;
        }

        // 10. Check for getWidgetSubIdList response
        if (rawMessage.includes('getWidgetSubIdList')) {
            const subResult = parseWidgetSubIdListResponse(rawMessage);
            if (subResult) {
                const existing = qlcStore.widgets[subResult.widgetId];
                if (existing) {
                    existing.subWidgets = subResult.subWidgets;
                }
                // If Animation, register presets in animation store
                if (existing && existing.type === 'Animation') {
                    qlcStore.setAnimationState(subResult.widgetId, {
                        presets: subResult.subWidgets,
                        selectedPresetId: subResult.subWidgets[0]?.id,
                    });
                }
            }
        }
    }

    // Connect to WebSocket endpoint
    function connect(pairingCode?: string): void {
        if (qlcStore.isMockMode) {
            qlcStore.setConnectionStatus({
                connected: true,
                mode: 'mock',
                statusText: 'Mock Modus Actief',
            });
            return;
        }

        if (socket.value && (socket.value.readyState === WebSocket.OPEN || socket.value.readyState === WebSocket.CONNECTING)) {
            return;
        }

        if (typeof window === 'undefined') {
            return;
        }

        isConnecting.value = true;
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        let wsUrl = `${protocol}//${window.location.host}/api/ws/client`;
        if (pairingCode && pairingCode.trim().length > 0) {
            wsUrl += `?code=${encodeURIComponent(pairingCode.trim())}`;
        }

        try {
            const ws = new WebSocket(wsUrl);
            socket.value = ws;

            ws.onopen = function () {
                isConnecting.value = false;
                qlcStore.setConnectionStatus({
                    connected: true,
                    reconnectAttempts: 0,
                    statusText: pairingCode ? `Koppelen met code ${pairingCode}...` : 'Verbonden',
                });
                if (pairingCode && pairingCode.trim().length > 0) {
                    try {
                        ws.send(JSON.stringify({
                            type: 'pair',
                            code: pairingCode.trim(),
                        }));
                    }
                    catch (err) {
                        console.error('[useQlcPlus] Failed sending pair message on open:', err);
                    }
                }
            };

            ws.onmessage = async function (event) {
                let data = event.data;
                if (typeof Blob !== 'undefined' && data instanceof Blob) {
                    data = await data.text();
                }
                if (typeof data === 'string') {
                    handleIncomingMessage(data);
                }
            };

            ws.onclose = function () {
                isConnecting.value = false;
                socket.value = null;
                qlcStore.setConnectionStatus({
                    connected: false,
                    statusText: 'Verbinding verbroken. Opnieuw proberen in 3 seconden...',
                });
                scheduleReconnect(pairingCode);
            };

            ws.onerror = function () {
                isConnecting.value = false;
                qlcStore.setConnectionStatus({
                    connected: false,
                    statusText: 'Verbindingsfout opgetreden',
                });
            };
        }
        catch (err) {
            console.error('[useQlcPlus] Failed initializing WebSocket:', err);
            scheduleReconnect(pairingCode);
        }
    }

    // Schedule reconnection
    function scheduleReconnect(pairingCode?: string): void {
        if (reconnectTimer.value || qlcStore.isMockMode) {
            return;
        }
        reconnectTimer.value = setTimeout(() => {
            reconnectTimer.value = undefined;
            qlcStore.connection.reconnectAttempts++;
            connect(pairingCode);
        }, 3000);
    }

    // Disconnect
    function disconnect(): void {
        if (reconnectTimer.value) {
            clearTimeout(reconnectTimer.value);
            reconnectTimer.value = undefined;
        }
        if (socket.value) {
            socket.value.close();
            socket.value = null;
        }
        qlcStore.setConnectionStatus({
            connected: false,
            statusText: 'Niet verbonden',
        });
    }

    // Pair with a specific 6-digit code
    function pairWithCode(code: string): void {
        const trimmed = code.trim();
        if (!trimmed) {
            return;
        }

        // If socket is already open, send dynamic pair request over existing connection
        if (socket.value && socket.value.readyState === WebSocket.OPEN) {
            console.log(`[useQlcPlus] Sending dynamic pair request for code: ${trimmed}`);
            try {
                socket.value.send(JSON.stringify({
                    type: 'pair',
                    code: trimmed,
                }));
            }
            catch (err) {
                console.error('[useQlcPlus] Failed sending dynamic pair command:', err);
            }
            return;
        }

        // If not connected, disconnect and connect with code
        disconnect();
        connect(trimmed);
    }

    // Toggle mock mode
    function toggleMock(enabled?: boolean): void {
        qlcStore.toggleMockMode(enabled);
        if (qlcStore.isMockMode) {
            disconnect();
        }
        else {
            connect();
        }
    }

    return {
        qlcStore,
        isConnecting,
        connect,
        disconnect,
        pairWithCode,
        pressButton,
        releaseButton,
        toggleButton,
        setFaderValue,
        selectAnimationPreset,
        triggerAnimationControl,
        setMatrixColor,
        setAnimationColors,
        setAnimationSpeed,
        setMatrixAlgorithm,
        setMatrixPower,
        toggleMatrix,
        broadcastGlobalColors,
        sendSpeedDialFactor,
        sendSpeedDialTime,
        sendSpeedDialStep,
        triggerBlackout,
        triggerHaptic,
        toggleMock,
        requestWidgetsDiscovery,
    };
}
